import i18n from '@/utils/i18n';
import { RecommendationActionEntry } from '@/experiences/sales-portal/domain/pricing-agent/blocs/stores/recommendation-actions-store-types';
import { toActionInput } from '@/experiences/sales-portal/domain/pricing-agent/blocs/utils/recommendation-action-input';
import { useCallback, useMemo } from 'react';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';
import { useUpsertPricingRecommendationsStatusMutation } from '@/experiences/sales-portal/domain/pricing-agent/blocs/documents/upsert-pricing-recommendations-status.generated';

/**
 * Persists per-recommendation status to the backend for auto-publish tasks
 * (save-progress), without applying prices. Distinct from the terminal
 * SubmitPricingRecommendationActions publish.
 */
export const useUpsertRecommendationStatusMutator = () => {
  const { showErrorToast } = useExperienceToasts();
  const [upsert, { loading }] = useUpsertPricingRecommendationsStatusMutation();

  const upsertActions = useCallback(
    async (entries: RecommendationActionEntry[]): Promise<boolean> => {
      if (entries.length === 0) {
        return false;
      }
      try {
        await upsert({ variables: { actions: entries.map(toActionInput) } });
        return true;
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : i18n.t('pricing.verify_edits.unknown_error');
        showErrorToast(errorMessage);
        return false;
      }
    },
    [showErrorToast, upsert],
  );

  return useMemo(() => ({ loading, upsertActions }), [loading, upsertActions]);
};
